import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';
import { App } from '@capacitor/app';
import { Purchases, CustomerInfo, PurchasesOffering } from '@revenuecat/purchases-capacitor';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

// Check if we're running in Capacitor
export const isCapacitor = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isAndroid = Capacitor.getPlatform() === 'android';
export const isWeb = Capacitor.getPlatform() === 'web';

// Push Notifications Service
export const pushNotifications = {
  // Request permissions and get FCM token (without registering to backend)
  async initialize() {
    if (!isCapacitor) return null;

    // Request permission using FirebaseMessaging (works for both iOS and Android)
    const permissionStatus = await FirebaseMessaging.requestPermissions();

    if (permissionStatus.receive !== 'granted') {
      throw new Error('User denied push notification permissions!');
    }

    // Get FCM token
    const result = await FirebaseMessaging.getToken();
    const fcmToken = result.token;

    console.log('✅ [Capacitor Push] FCM token obtained:', fcmToken.substring(0, 20) + '...');

    // Set up notification listeners
    // Handle notification received
    FirebaseMessaging.addListener('notificationReceived', (notification) => {
      console.log('🔔 [Capacitor Push] Notification received:', notification);
    });

    // Handle notification tap — navigate using the url field in notification data
    FirebaseMessaging.addListener('notificationActionPerformed', (notification) => {
      console.log('👆 [Capacitor Push] Notification tapped:', notification);
      const data = notification.notification.data;

      // Special handling for invite deep links (invite code, not a URL)
      if (data?.type === 'league_invite' && data.inviteCode) {
        window.location.href = `/invite/${data.inviteCode}`;
        return;
      }

      // Universal: use the url field present in all notification payloads
      if (data?.url) {
        try {
          const url = new URL(data.url);
          const search = url.search || '';
          // Capacitor's local webview may strip query params on navigation,
          // so persist them via sessionStorage for the target page to read
          if (search) {
            sessionStorage.setItem('yapzap_deeplink_params', search);
          }
          // Convert full URL to relative path for client-side navigation
          window.location.href = url.pathname + search;
        } catch {
          // If it's already a relative path, use directly
          window.location.href = data.url;
        }
        return;
      }

      // Fallback: navigate to league if leagueId is present
      if (data?.leagueId) {
        window.location.href = `/leagues/${data.leagueId}`;
      }
    });

    // Return the token so the hook can register it with auth
    return fcmToken;
  },

  // Set up listener for token refresh (called after initial setup)
  setupTokenRefreshListener(onTokenRefresh: (token: string) => void) {
    if (!isCapacitor) return;

    FirebaseMessaging.addListener('tokenReceived', (event) => {
      console.log('🔄 [Capacitor Push] Token refreshed:', event.token.substring(0, 20) + '...');
      onTokenRefresh(event.token);
    });
  },

  async scheduleLocal(options: {
    title: string;
    body: string;
    id: number;
    schedule?: { at: Date };
    extra?: any;
  }) {
    if (!isCapacitor) return;
    
    await LocalNotifications.schedule({
      notifications: [{
        ...options,
        schedule: options.schedule
      }]
    });
  }
};

// In-App Purchases Service
export const purchases = {
  async initialize(apiKey: string) {
    if (!isCapacitor) return;
    
    await Purchases.configure({
      apiKey,
      appUserID: null // Will use anonymous ID
    });
  },

  async getOfferings(): Promise<PurchasesOffering[]> {
    if (!isCapacitor) return [];
    
    const offerings = await Purchases.getOfferings();
    return offerings.current ? [offerings.current] : [];
  },

  async purchase(productId: string): Promise<CustomerInfo> {
    if (!isCapacitor) throw new Error('Not available on web');
    
    const offerings = await Purchases.getOfferings();
    const product = offerings.current?.availablePackages.find(
      pkg => pkg.product.identifier === productId
    );
    
    if (!product) {
      throw new Error('Product not found');
    }
    
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: product });
    return customerInfo;
  },

  async restorePurchases(): Promise<CustomerInfo> {
    if (!isCapacitor) throw new Error('Not available on web');
    
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo;
  },

  async getCustomerInfo(): Promise<CustomerInfo> {
    if (!isCapacitor) throw new Error('Not available on web');
    
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  },

  async checkSubscription(): Promise<boolean> {
    if (!isCapacitor) return false;
    
    try {
      const customerInfo = await this.getCustomerInfo();
      return customerInfo.entitlements.active['premium'] !== undefined;
    } catch {
      return false;
    }
  }
};

// App Utilities
export const appUtils = {
  async openUrl(url: string) {
    if (isCapacitor) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
  },

  async share(options: { title?: string; text?: string; url?: string }) {
    if (isCapacitor && (isIOS || isAndroid)) {
      await Share.share(options);
    } else if (navigator.share) {
      await navigator.share(options);
    } else {
      // Fallback: copy to clipboard
      const text = `${options.title || ''} ${options.text || ''} ${options.url || ''}`.trim();
      await navigator.clipboard.writeText(text);
      alert('Link copied to clipboard!');
    }
  },

  async addListener(event: 'appStateChange' | 'backButton', callback: (data: any) => void) {
    if (!isCapacitor) return;
    
    return App.addListener(event, callback);
  },

  async exitApp() {
    if (isCapacitor && isAndroid) {
      App.exitApp();
    }
  }
};

// CarPlay Integration
export const carPlay = {
  isAvailable: isCapacitor && isIOS,

  /**
   * Pass the Firebase auth token to the CarPlay plugin
   * This should be called when the user logs in
   */
  async setAuthToken(token: string) {
    if (!isCapacitor || !isIOS) {
      console.log('🚗 [CarPlay] Not on iOS native platform, skipping setAuthToken');
      return;
    }

    try {
      const { registerPlugin } = await import('@capacitor/core');
      const YapZapCarPlay = registerPlugin<any>('YapZapCarPlay');

      console.log('🚗 [CarPlay] Setting auth token...');
      await YapZapCarPlay.setAuthToken({ token });
      console.log('🚗 [CarPlay] ✓ Auth token set successfully');
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to set auth token:', error);
    }
  },

  /**
   * Configure music service availability for CarPlay.
   * Must be called after setAuthToken to tell the native layer whether
   * the user has Apple Music connected in YapZap (separate from iOS system auth).
   */
  async setMusicServiceConfig(options: { appleMusicConnected: boolean }) {
    if (!isCapacitor || !isIOS) return;
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const YapZapCarPlay = registerPlugin<any>('YapZapCarPlay');
      await YapZapCarPlay.setMusicServiceConfig(options);
      console.log('🚗 [CarPlay] Music service config set:', options);
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to set music service config:', error);
    }
  },

  /**
   * Check if CarPlay is currently connected
   */
  async isConnected(): Promise<boolean> {
    if (!isCapacitor || !isIOS) {
      return false;
    }

    try {
      const { registerPlugin } = await import('@capacitor/core');
      const YapZapCarPlay = registerPlugin<any>('YapZapCarPlay');

      const result = await YapZapCarPlay.isConnected();
      return result.connected;
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to check connection:', error);
      return false;
    }
  },

  /**
   * Refresh the current voting round data in CarPlay
   */
  async refreshRound() {
    if (!isCapacitor || !isIOS) {
      return;
    }

    try {
      const { registerPlugin } = await import('@capacitor/core');
      const YapZapCarPlay = registerPlugin<any>('YapZapCarPlay');

      console.log('🚗 [CarPlay] Refreshing round data...');
      await YapZapCarPlay.refreshRound();
      console.log('🚗 [CarPlay] ✓ Round refreshed');
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to refresh round:', error);
    }
  },

  /**
   * Add a listener for CarPlay events
   */
  async addListener(event: 'carplayConnected' | 'carplayDisconnected' | 'voteSubmitted' | 'openInMusicService', callback: (data?: any) => void) {
    if (!isCapacitor || !isIOS) {
      return { remove: () => {} };
    }

    try {
      const { registerPlugin } = await import('@capacitor/core');
      const YapZapCarPlay = registerPlugin<any>('YapZapCarPlay');

      return YapZapCarPlay.addListener(event, callback);
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to add listener:', error);
      return { remove: () => {} };
    }
  }
};

export const androidAuto = {
  isAvailable: false,
  // Would need native implementation
};